import { useContext, useMemo } from 'react'
import { t } from 'i18next'
import { FormInputProps, SelectOption } from '@microservices/wiskey-react-components'
import { FormInputsType } from '@microservices/wiskey-react-components/dist/types'

import { FieldType, SeriesType } from '@pages/DiagramsCreateOrEdit/types'
import { PageContext } from '@pages/EntityCreateOrEdit'

import { BIND_TYPE, GENERATOR_INPUT_TYPE, MAX_INPUT_LENGTH, MODAL_TYPE, REGEX } from '@constants'
import { AutocompleteOption } from '@types'

import { columnBindTypes } from '../../../../../types/api/barChart.api'

type useInputsParams = {
  watchIsCommandForEverySeries: boolean
  watchJSForAll: boolean
  watchOperation: SelectOption
  objects: AutocompleteOption[]
  commandOptions: AutocompleteOption[]
  watchFields: FieldType[]
  objectFields: AutocompleteOption[]
  setValue: any
  toggleOpenScriptValueDialog: (value: boolean) => void
  setCurrentNameScriptDialog: (value: string) => void
  series: SeriesType[]
  handleChangeObject: (value: AutocompleteOption) => void
}

export const useInputs = ({
  watchIsCommandForEverySeries,
  watchOperation,
  watchJSForAll,
  commandOptions,
  objects,
  watchFields,
  objectFields,
  setValue,
  toggleOpenScriptValueDialog,
  setCurrentNameScriptDialog,
  series,
  handleChangeObject,
}: useInputsParams) => {
  const { modalType } = useContext(PageContext)
  const seriesOptions: AutocompleteOption[] = useMemo(() => {
    // const selectedSeriesIds = watchFields.map(({ seriesId }) => seriesId)

    return (
      series
        // .filter(singleSeries => selectedSeriesIds.indexOf(singleSeries.id) !== -1)
        .map(({ id, title }) => ({
          id,
          label: title,
        }))
    )
  }, [])

  const columnInputs: FormInputProps[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('diagramsCreateOrEdit.columns.code'),
      label: t('diagramsCreateOrEdit.columns.code'),
      disabled: modalType === MODAL_TYPE.EDIT,
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      rules: {
        required: true,
      },
    },
    {
      name: 'title',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('diagramsCreateOrEdit.columns.title'),
      label: t('diagramsCreateOrEdit.columns.title'),
      rules: {
        required: true,
      },
    },
    {
      name: 'object',
      autocompleteOptions: objects,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      onChangeAutocomplete: handleChangeObject,
      rules: {
        required: true,
      },
      label: t('diagramsCreateOrEdit.columns.object'),
      placeholder: t('diagramsCreateOrEdit.columns.object'),
    },
    {
      name: 'command',
      autocompleteOptions: commandOptions,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      rules: {
        required: true,
      },
      label: t('diagramsCreateOrEdit.columns.command'),
      placeholder: t('diagramsCreateOrEdit.columns.command'),
    },
  ]

  const valueFieldForJsBinding: FormInputsType[] = [
    {
      name: 'jsCreateValue',
      inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
      placeholder: t('placeholder.fieldValue'),
      label: t('label.value'),
      readOnly: true,
      rules: {
        required: true,
      },
      additionalBtn: {
        isEnabled: true,
        text: 'edit',
        color: 'primary',
        variant: 'contained',
        onClick: () => {
          setCurrentNameScriptDialog('jsCreateValue')
          toggleOpenScriptValueDialog(true)
        },
      },
    },
  ]
  const columnAdditionalFieldInputs = [
    {
      name: 'commandForEverySeries',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: t('diagramsCreateOrEdit.columns.useCommandForEverySeries'),
      labelSx: { width: 230 },
      labelPlacement: 'end',
      disabled: watchJSForAll,
    },
    {
      name: 'jsForAllFields',
      disabled: watchIsCommandForEverySeries,
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: t('diagramsCreateOrEdit.columns.useJS'),
      labelSx: { width: 180 },
      labelPlacement: 'end',
    },
    ...(watchJSForAll || watchIsCommandForEverySeries ? valueFieldForJsBinding : []),
  ]

  const columnFieldInputs = watchFields.map((field, index) => ({
    name: `fields.${index}`,
    key: field.id || 'field ' + index,
    inputs: [
      {
        name: `fields.${index}.title`,
        inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
        label: t('diagramsCreateOrEdit.columns.series'),
        placeholder: t('diagramsCreateOrEdit.columns.series'),
        disabled: true,
        isOptionEqualToValue: (option, value) => option.id === value.id,
        value: seriesOptions.find(({ id }) => id === field.seriesId),
        autocompleteOptions: seriesOptions,
        onChangeAutocomplete: value => {
          setValue(`fields.${index}.title`, value.label)
          setValue(`fields.${index}.seriesId`, value.id)
        },
        disableClearable: true,
      },
      {
        name: `fields.${index}.bindType`,
        inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
        label: t('diagramsCreateOrEdit.columns.binding'),
        placeholder: t('diagramsCreateOrEdit.columns.binding'),
        disableClearable: true,
        autocompleteOptions: columnBindTypes,
        value: field.bindType,
        onChangeAutocomplete: () => {
          setValue(`fields.${index}.value`, null)
        },
      },
      field.bindType.id === BIND_TYPE.FIELD
        ? {
            name: `fields.${index}.value`,
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            label: t('diagramsCreateOrEdit.columns.fields'),
            autocompleteOptions: objectFields,
            placeholder: t('diagramsCreateOrEdit.columns.fields'),
            value: field.value?.label
              ? field.value
              : objectFields?.find(({ label }) => label === field.value),
          }
        : {
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.INPUT,
            label: t('commandParameters.inputs.value.label'),
            labelSx: { minWidth: 60, width: 60 },
            name: `fields.${index}.value`,
            placeholder: t('commandParameters.inputs.value.placeholder'),
            readOnly: true,
            additionalBtn: {
              isEnabled: true,
              text: 'edit',
              color: 'primary',
              variant: 'contained',
              onClick: () => {
                toggleOpenScriptValueDialog(true)
                setCurrentNameScriptDialog(`fields.${index}.value`)
              },
            },
            formInputContainerSx: {
              flexWrap: 'nowrap',
              '& .MuiFormControlLabel-root': {
                mb: 1,
              },
              '& .MuiButton-root': {
                ml: 0.5,
              },
            },
            rules: {
              maxLength: MAX_INPUT_LENGTH,
            },
          },
    ],
  }))

  return {
    columnInputs,
    columnAdditionalFieldInputs,
    columnFieldInputs,
  }
}
